import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import useThemeStore from '../stores/themeStore'

import { createLogger } from '../utils/logger'

const logger = createLogger('useDesmos')

// Constants - API version can be overridden via environment variable
const DESMOS_CONSTANTS = {
  API_VERSION: import.meta.env.VITE_DESMOS_API_VERSION || '1.11',
  DEFAULT_FONT_SIZE: 12
}

// Desmos API key from Vite environment
// __DESMOS_API_KEY__ is injected by Vite via define config (see vite.config.js)
// eslint-disable-next-line no-undef
const DESMOS_API_KEY = typeof __DESMOS_API_KEY__ !== 'undefined' ? __DESMOS_API_KEY__ : ''

// Track loading state globally with explicit state machine
// States: 'idle' | 'loading' | 'loaded' | 'failed'
let desmosLoadState = 'idle'
let desmosLoadPromise = null

/**
 * Load Desmos API script dynamically
 * @returns {Promise<boolean>} - Whether loading was successful
 */
function loadDesmosScript() {
  // Already loaded successfully
  if (desmosLoadState === 'loaded' && window.Desmos) {
    return Promise.resolve(true)
  }

  // Currently loading - return existing promise
  if (desmosLoadState === 'loading' && desmosLoadPromise) {
    return desmosLoadPromise
  }

  // No API key configured
  if (!DESMOS_API_KEY) {
    logger.warn('[Desmos] API key not configured - Desmos features disabled')
    desmosLoadState = 'failed'
    return Promise.resolve(false)
  }

  // Reset state if previous attempt failed (allow retry)
  if (desmosLoadState === 'failed') {
    desmosLoadState = 'idle'
    desmosLoadPromise = null
  }

  // IMPORTANT: Set state to 'loading' BEFORE creating the promise
  // This closes the race window where multiple calls could start loading simultaneously
  // between the state check at line 31 and promise creation
  desmosLoadState = 'loading'

  // Check if script already exists in DOM BEFORE creating promise
  // This handles the case where script was added by another instance
  const existingScript = document.querySelector('script[src*="desmos.com/api"]')
  if (existingScript && window.Desmos) {
    logger.debug('[Desmos] Calculator API already available')
    desmosLoadState = 'loaded'
    window.__desmosLoaded__ = true
    desmosLoadPromise = Promise.resolve(true)
    return desmosLoadPromise
  }

  desmosLoadPromise = new Promise((resolve) => {
    // Double-check: script might have been added while we were checking
    const scriptInDom = document.querySelector('script[src*="desmos.com/api"]')
    if (scriptInDom && window.Desmos) {
      logger.debug('[Desmos] Calculator API already available')
      desmosLoadState = 'loaded'
      window.__desmosLoaded__ = true
      resolve(true)
      return
    }

    const script = document.createElement('script')
    script.src = `https://www.desmos.com/api/v${DESMOS_CONSTANTS.API_VERSION}/calculator.js?apiKey=${encodeURIComponent(DESMOS_API_KEY)}`
    script.async = true

    script.onload = () => {
      logger.debug('[Desmos] Calculator API loaded successfully')
      desmosLoadState = 'loaded'
      window.__desmosLoaded__ = true
      resolve(true)
    }

    script.onerror = () => {
      logger.error('[Desmos] Failed to load Calculator API')
      desmosLoadState = 'failed'
      window.__desmosLoaded__ = false
      desmosLoadPromise = null // Allow retry on next call
      resolve(false)
    }

    document.head.appendChild(script)
  })

  return desmosLoadPromise
}

/**
 * Hook to check if Desmos API is available
 * @returns {boolean} - Whether Desmos is loaded and available
 */
export function useDesmosAvailable() {
  const [isAvailable, setIsAvailable] = useState(desmosLoadState === 'loaded' && !!window.Desmos)

  useEffect(() => {
    // Check if already loaded
    if (desmosLoadState === 'loaded' && window.Desmos) {
      setIsAvailable(true)
      return
    }

    // Try to load the script
    let mounted = true
    loadDesmosScript().then((success) => {
      if (mounted) {
        setIsAvailable(success && !!window.Desmos)
      }
    })

    return () => {
      mounted = false
    }
  }, [])

  return isAvailable
}

/**
 * Hook to create and manage a Desmos calculator instance
 * @param {Object} options - Configuration options
 * @param {Object|null} options.initialState - Initial Desmos state to load
 * @param {boolean} options.interactive - Whether the calculator should be interactive (default: true)
 * @param {boolean} options.showExpressions - Whether to show the expressions list (default: true)
 * @param {boolean} options.showKeypad - Whether to show the keypad (default: false)
 * @param {boolean} options.showSettings - Whether to show the settings menu (default: false)
 * @param {boolean} options.lockViewport - Whether to lock viewport panning/zooming (default: false)
 * @param {'graphing'|'scientific'} options.calculatorType - Calculator type (default: 'graphing')
 * @returns {Object} - Calculator controls and state
 */
export function useDesmos({
  initialState = null,
  interactive = true,
  showExpressions = true,
  showKeypad = false,
  showSettings = false,
  lockViewport = false,
  calculatorType = 'graphing'
} = {}) {
  // Use state to track the container element so we can trigger effects when it's mounted
  const [containerElement, setContainerElement] = useState(null)
  const calculatorRef = useRef(null)
  const [isReady, setIsReady] = useState(false)
  const [error, setError] = useState(null)

  // Callback ref that updates state when container is mounted/unmounted
  const containerRef = useCallback((node) => {
    setContainerElement(node)
  }, [])

  const effectiveTheme = useThemeStore((state) => state.effectiveTheme)
  const isDarkMode = effectiveTheme === 'dark'

  // Memoize initial options to detect if they truly changed
  const initialOptions = useMemo(
    () => ({
      showKeypad,
      showExpressions,
      showSettings,
      lockViewport,
      interactive
    }),
    [] // Only compute once on mount
  )

  // Initialize calculator when container is available and Desmos is loaded
  // This effect runs when containerElement changes (via callback ref)
  useEffect(() => {
    if (!containerElement) {
      return
    }

    let mounted = true
    let localCalculator = null

    // Ensure Desmos script is loaded, then initialize
    loadDesmosScript().then((success) => {
      // IMPORTANT: Check mounted FIRST before any other operations
      // This prevents creating calculators that would be orphaned if component unmounted
      // during the async loadDesmosScript() call
      if (!mounted) {
        return
      }

      if (!success || !window.Desmos || !containerElement) {
        if (!success) {
          setError('Failed to load Desmos API')
        }
        return
      }

      try {
        // Double-check mounted status before creating calculator
        // This closes the race window between the initial check and creation
        if (!mounted) {
          return
        }

        // Destroy existing calculator if any
        if (calculatorRef.current) {
          calculatorRef.current.destroy()
          calculatorRef.current = null
        }

        // Create new calculator instance based on type
        if (calculatorType === 'scientific' && window.Desmos.ScientificCalculator) {
          localCalculator = window.Desmos.ScientificCalculator(containerElement, {
            degreeMode: true,
            invertedColors: isDarkMode
          })
        } else {
          localCalculator = window.Desmos.GraphingCalculator(containerElement, {
            // Display options
            border: false,
            keypad: initialOptions.showKeypad,
            expressions: initialOptions.showExpressions,
            settingsMenu: initialOptions.showSettings,
            zoomButtons: initialOptions.interactive,
            expressionsTopbar: initialOptions.showExpressions,

            // Interactivity
            lockViewport: initialOptions.lockViewport || !initialOptions.interactive,
            expressionsCollapsed: !initialOptions.showExpressions,

            // Feature toggles
            sliders: initialOptions.interactive,
            images: true,
            folders: false,
            notes: false,
            links: false,
            trace: initialOptions.interactive,
            pointsOfInterest: initialOptions.interactive,

            // Styling
            fontSize: window.Desmos.FontSizes?.SMALL || DESMOS_CONSTANTS.DEFAULT_FONT_SIZE,
            invertedColors: isDarkMode,

            // Language
            language: 'en'
          })

          // Load initial state if provided (only for graphing calculator)
          if (initialState) {
            try {
              localCalculator.setState(initialState)
            } catch (stateError) {
              logger.error('[Desmos] Failed to load initial state:', stateError)
              if (mounted) {
                setError('Failed to load graph state')
              }
            }
          }
        }

        // Only assign ref and update state if still mounted
        // This prevents race condition where ref is assigned after unmount begins
        if (mounted) {
          calculatorRef.current = localCalculator
          setIsReady(true)
          setError(null)

          // IMPORTANT: Resize calculator after DOM paint to ensure proper dimensions
          // This fixes the blank calculator issue on first open when the modal
          // container hasn't fully rendered yet
          // Double RAF ensures layout is complete (first RAF waits for paint, second ensures layout)
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              if (mounted && localCalculator) {
                try {
                  localCalculator.resize()
                } catch (resizeErr) {
                  logger.debug('[Desmos] Initial resize failed:', resizeErr.message)
                }
              }
            })
          })
        }
      } catch (err) {
        logger.error('[Desmos] Failed to initialize calculator:', err)
        if (mounted) {
          setError('Failed to initialize calculator')
          setIsReady(false)
        }
      }
    })

    // Cleanup on unmount - use local variable first, fall back to ref
    return () => {
      mounted = false
      const calcToDestroy = localCalculator || calculatorRef.current
      if (calcToDestroy) {
        try {
          calcToDestroy.destroy()
        } catch (err) {
          logger.error('[Desmos] Error destroying calculator:', err)
        }
      }
      calculatorRef.current = null
      // Note: Don't call setIsReady(false) here - component is unmounting
      // and calling setState after unmount causes React warnings
    }
  }, [containerElement, initialOptions, isDarkMode, calculatorType]) // Note: initialState handled separately in effect below

  // Update settings when options change (without recreating calculator)
  useEffect(() => {
    if (!calculatorRef.current) return

    calculatorRef.current.updateSettings({
      keypad: showKeypad,
      expressions: showExpressions,
      settingsMenu: showSettings,
      lockViewport: lockViewport || !interactive,
      zoomButtons: interactive,
      expressionsTopbar: showExpressions,
      expressionsCollapsed: !showExpressions,
      sliders: interactive,
      trace: interactive,
      pointsOfInterest: interactive
    })
  }, [showKeypad, showExpressions, showSettings, lockViewport, interactive])

  // Sync dark mode changes
  useEffect(() => {
    if (calculatorRef.current) {
      calculatorRef.current.updateSettings({
        invertedColors: isDarkMode
      })
    }
  }, [isDarkMode])

  // Load state if initialState changes after initial mount
  useEffect(() => {
    if (calculatorRef.current && isReady && initialState) {
      try {
        calculatorRef.current.setState(initialState)
      } catch (err) {
        logger.error('[Desmos] Failed to update state:', err)
      }
    }
  }, [initialState, isReady])

  // Get current state
  const getState = useCallback(() => {
    if (calculatorRef.current) {
      return calculatorRef.current.getState()
    }
    return null
  }, [])

  // Set expression
  const setExpression = useCallback((expression) => {
    if (calculatorRef.current) {
      calculatorRef.current.setExpression(expression)
    }
  }, [])

  // Set math bounds
  const setMathBounds = useCallback((bounds) => {
    if (calculatorRef.current) {
      calculatorRef.current.setMathBounds(bounds)
    }
  }, [])

  // Reset to blank
  const reset = useCallback(() => {
    if (calculatorRef.current) {
      calculatorRef.current.setBlank()
    }
  }, [])

  // Take screenshot
  const screenshot = useCallback((options = {}) => {
    if (calculatorRef.current) {
      return calculatorRef.current.screenshot(options)
    }
    return null
  }, [])

  // Resize calculator
  const resize = useCallback(() => {
    if (calculatorRef.current) {
      calculatorRef.current.resize()
    }
  }, [])

  return {
    containerRef,
    calculator: calculatorRef.current,
    isReady,
    error,
    getState,
    setExpression,
    setMathBounds,
    reset,
    screenshot,
    resize
  }
}

export default useDesmos
